import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export interface RoomSaleAdminEntry {
  floor?: number | null;
  room_id: string;
  room_type_id: string;
  room_name: string;
  room_type_name: string;
  product: string | null;
  channel: string | null;
  first_move_in_date: string | null;
  longterm_contract_end_date: string | null;
  daily_price_minor: number | null;
  price_currency: string | null;
  price_source: string | null;
  price_revision: number | null;
  allowed_actions: string[];
  residual_booking_count: number;
  residual_booking_last_date: string | null;
  status: string;
  reasons: string[];
  last_success_at: string | null;
  sale_revision: number | null;
  pending_outcome: string | null;
  pending_observed_at: string | null;
}

export interface RoomSalesPage {
  property_id: string;
  property_name: string;
  items: RoomSaleAdminEntry[];
  next_cursor: string | null;
}

export interface RoomSaleAdminDetail {
  room_id: string;
  product: string;
  channel: string;
  first_move_in_date: string | null;
  revision: number;
  pending_outcome: string | null;
  pending_observed_at: string | null;
  midterm_reentry_blocked: boolean;
  daily_price_minor: number | null;
  price_currency: string | null;
  price_source: string | null;
  price_revision: number | null;
  status: string | null;
  reasons: string[];
  allowed_actions: string[];
}

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const keysAre = (value: RecordValue, keys: string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const integer = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) >= 0;
const cursor = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length >= 1 &&
  value.length <= 2048 &&
  /^[A-Za-z0-9_-]+$/.test(value);
const nullableInteger = (value: unknown): value is number | null =>
  value === null || integer(value);
const date = (value: unknown): value is string | null => {
  if (value === null) return true;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};
const nullableDateTime = (value: unknown): value is string | null =>
  value === null || isOffsetDateTime(value);
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(text);
const sameUuid = (value: unknown, expected: string): value is string =>
  text(value) && value.toLowerCase() === expected.toLowerCase();

const entryKeys = [
  'room_id',
  'room_type_id',
  'room_name',
  'room_type_name',
  'product',
  'channel',
  'first_move_in_date',
  'longterm_contract_end_date',
  'daily_price_minor',
  'price_currency',
  'price_source',
  'price_revision',
  'allowed_actions',
  'residual_booking_count',
  'residual_booking_last_date',
  'status',
  'reasons',
  'last_success_at',
  'sale_revision',
  'pending_outcome',
  'pending_observed_at',
];
const detailKeys = [
  'room_id',
  'product',
  'channel',
  'first_move_in_date',
  'revision',
  'pending_outcome',
  'pending_observed_at',
  'midterm_reentry_blocked',
  'daily_price_minor',
  'price_currency',
  'price_source',
  'price_revision',
  'status',
  'reasons',
  'allowed_actions',
];

function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '객실 판매 현황을 확인하지 못했습니다.',
  });
}

function parseEntry(value: unknown): RoomSaleAdminEntry {
  if (
    !record(value) ||
    !keysAre(value, [...entryKeys, ...('floor' in value ? ['floor'] : [])]) ||
    ('floor' in value &&
      value.floor !== null &&
      (!Number.isInteger(value.floor) ||
        Number(value.floor) < -(2 ** 31) ||
        Number(value.floor) >= 2 ** 31)) ||
    !isUuid(value.room_id) ||
    !isUuid(value.room_type_id) ||
    !text(value.room_name) ||
    !text(value.room_type_name) ||
    !nullableText(value.product) ||
    !nullableText(value.channel) ||
    !date(value.first_move_in_date) ||
    !date(value.longterm_contract_end_date) ||
    !nullableInteger(value.daily_price_minor) ||
    !nullableText(value.price_currency) ||
    !nullableText(value.price_source) ||
    !nullableInteger(value.price_revision) ||
    !strings(value.allowed_actions) ||
    !integer(value.residual_booking_count) ||
    !date(value.residual_booking_last_date) ||
    !text(value.status) ||
    !strings(value.reasons) ||
    !nullableDateTime(value.last_success_at) ||
    !nullableInteger(value.sale_revision) ||
    !nullableText(value.pending_outcome) ||
    !nullableDateTime(value.pending_observed_at)
  )
    throw invalid();
  return value as unknown as RoomSaleAdminEntry;
}

function parseDetail(value: unknown, roomId: string): RoomSaleAdminDetail {
  if (
    !record(value) ||
    !keysAre(value, detailKeys) ||
    !sameUuid(value.room_id, roomId) ||
    !text(value.product) ||
    !text(value.channel) ||
    !date(value.first_move_in_date) ||
    !integer(value.revision) ||
    !nullableText(value.pending_outcome) ||
    !nullableDateTime(value.pending_observed_at) ||
    typeof value.midterm_reentry_blocked !== 'boolean' ||
    !nullableInteger(value.daily_price_minor) ||
    !nullableText(value.price_currency) ||
    !nullableText(value.price_source) ||
    !nullableInteger(value.price_revision) ||
    !nullableText(value.status) ||
    !strings(value.reasons) ||
    !strings(value.allowed_actions)
  )
    throw invalid();
  return value as unknown as RoomSaleAdminDetail;
}

export async function readRoomSales(
  client: ServiceClient,
  propertyId: string,
  options: { limit: number; cursor: string | null; roomId?: string; roomTypeId?: string },
  signal?: AbortSignal,
): Promise<RoomSalesPage> {
  if (
    !isUuid(propertyId) ||
    (options.roomId !== undefined &&
      (!isUuid(options.roomId) || options.roomId !== options.roomId.toLowerCase())) ||
    (options.roomTypeId !== undefined &&
      (!isUuid(options.roomTypeId) || options.roomTypeId !== options.roomTypeId.toLowerCase())) ||
    !Number.isInteger(options.limit) ||
    options.limit < 1 ||
    options.limit > 50 ||
    (options.cursor !== null && !cursor(options.cursor))
  )
    throw invalid();
  const raw = await client.get<unknown>(`admin/properties/${propertyId}/room-sales`, {
    params: {
      limit: options.limit,
      ...(options.cursor ? { cursor: options.cursor } : {}),
      room_id: options.roomId,
      room_type_id: options.roomTypeId,
    },
    signal,
  });
  if (
    !record(raw) ||
    !keysAre(raw, ['property_id', 'property_name', 'items', 'next_cursor']) ||
    !sameUuid(raw.property_id, propertyId) ||
    !text(raw.property_name) ||
    !Array.isArray(raw.items) ||
    !(raw.next_cursor === null || cursor(raw.next_cursor))
  )
    throw invalid();
  const items = raw.items.map(parseEntry);
  if (
    (options.roomId !== undefined &&
      (raw.next_cursor !== null ||
        items.length > 1 ||
        items.some((item) => item.room_id !== options.roomId))) ||
    (options.roomTypeId !== undefined &&
      items.some((item) => item.room_type_id !== options.roomTypeId))
  )
    throw invalid();
  if (new Set(items.map((item) => item.room_id.toLowerCase())).size !== items.length)
    throw invalid();
  return {
    property_id: raw.property_id,
    property_name: raw.property_name,
    items,
    next_cursor: raw.next_cursor,
  };
}

export async function readRoomSale(
  client: ServiceClient,
  roomId: string,
  signal?: AbortSignal,
): Promise<RoomSaleAdminDetail> {
  if (!isUuid(roomId)) throw invalid();
  return parseDetail(
    await client.get<unknown>(`management/booking/rooms/${roomId}/sale`, { signal }),
    roomId,
  );
}
